/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 *
 */

export const KINETIC_ENERGY_COLOR = '#FFA07A';

export const POTENTIAL_ENERGY_COLOR = '#00BFFF';

export const TOTAL_ENERGY_COLOR = '#00FF7F';

export const VAN_DER_WAALS_COLOR = '#FFFFFF';

export const DATA_QUEUE_LENGTH = 100;

export const LJ_SIGMA_CONVERTER = 2 / Math.sqrt(Math.cbrt(2));

// Coulomb's constant times the square of electron charge (ke^2), in eV*angstrom.
// see http://hyperphysics.phy-astr.gsu.edu/hbase/electric/elefor.html
export const COULOMB_CONSTANT = 8.99 * 1.6;

// converts energy gradient unit into force unit:
// 1.6E-19 [J] / ( E-10 [m] x E-3 / 6E23 [kg] ) / ( E-10 / ( E-15 ) ^2 ) [m/s^2]
export const GF_CONVERSION_CONSTANT = 0.0096;

export const SIX_TIMES_UNIT_FORCE = 6 * GF_CONVERSION_CONSTANT;

export const MIN_SIN_THETA = 0.001;

// convert mvv into eV: ( E-3 / 6E23 ) [kg] x ( E-10 / E-15 )^2 [m^2/s^2] / 1.6E-19 [J]
export const EV_CONVERTER = 1000 / (1.6 * 6);

// converts electron volt into kelvin
export const UNIT_EV_OVER_KB = 16000 / 1.38;

// Per-component Maxwell-Boltzmann velocity scale, in angstrom/femtosecond:
//   v_x_std(T, m) = VT_CONVERSION_CONSTANT · sqrt(T / m),  with m in g/mol, T in K.
// From equipartition ⟨½·m·v_x²⟩ = ½·k_B·T and the energy unit conversion EV_CONVERTER
// (which turns m·v² into eV), the scale is
//   VT² = k_B / EV_CONVERTER = 1 / (UNIT_EV_OVER_KB · EV_CONVERTER).
// Numerically ≈ 9.10e-4 (the legacy value of 2.882e-5 was √1000 too small —
// the integrator's thermostat would rescale toward the setpoint anyway, so the
// bug stayed hidden in NVT runs but biased the cold-start KE in NVE runs).
export const VT_CONVERSION_CONSTANT = Math.sqrt(1 / (UNIT_EV_OVER_KB * EV_CONVERTER));

// convert m/s^2 to angstrom / fs^2
export const GRAVITY_CONVERSION_CONSTANT = 1e-20;

// Convert a piston load P·A (pressure in Pa × area in Å²) into an engine force
// in amu·Å/fs² (the unit of Atom acceleration). Derivation:
//   F[N] = P[Pa]·A[Å²]·1e-20 (Å²→m²);  1 N = 1/1.66054e-27 amu·Å/fs² · 1e-20
//   ⇒ F[amu·Å/fs²] = P·A · (1e-40 / 1.66054e-27) = P·A · 6.02214e-14.
export const PISTON_FORCE_CONVERSION = 6.02214e-14;

// Boltzmann constant 1.380649E-23 m^2kg/s^2/K / E-30 m^3 = 1.38E7 kg/m/K/s^2 = 1.38E7 Pa/K
export const PRESSURE_CONVERSION_CONSTANT = 1.38e7;

// virial to pressure: mass*acceleration*position 1/6.022E26 kg * E-10/E-30 m/s^2 * E-10 m / E-30 m^3 = 1/6.022 E14 Pa
export const VIRIAL_CONVERSION_CONSTANT = 1e14 / 6.022;

// au/A^3 -> kg/m^3 (E30/6.022E26)
export const DENSITY_CONVERSION_CONSTANT = 10000 / 6.022;

// A/fs -> m/s
export const SPEED_CONVERTER = 100000;

// how many bins do we want in the speed distribution graph
export const SPEED_BIN_NUMBER = 20;
